import { useEffect, useState } from "preact/hooks"
import { logout, resendVerification, verifyEmail } from "./lib/api"
import { PublicShell as Shell } from "./PublicShell"
import { SiteFooter } from "./SiteFooter"

// The two halves of email verification.
//
// VerifyEmail is the page the emailed link opens (/verify?token=…). It is a
// public route: the link is routinely opened in a different browser — or on
// a different device — from the one that signed up, so the token is the only
// proof it needs, and it must render for a signed-out visitor.
//
// VerifyNotice is what a signed-in but unverified account sees instead of
// the app. The API refuses that session everywhere except the verification
// endpoints, so there is nothing else to show it.

export function VerifyEmail() {
	const [state, setState] = useState<"working" | "done" | "failed">("working")
	const [message, setMessage] = useState("")

	useEffect(() => {
		const token = new URLSearchParams(window.location.search).get("token")
		if (!token) {
			setState("failed")
			setMessage("This link is missing its verification code.")
			return
		}
		verifyEmail(token)
			.then((res) => {
				setState("done")
				setMessage(res.username)
			})
			.catch((err: unknown) => {
				setState("failed")
				setMessage(
					err instanceof Error ? err.message : "Could not verify this link",
				)
			})
	}, [])

	return (
		<Shell>
			{state === "working" && (
				<div class="flex center gap-2">
					<div class="spinner" role="status" />
					<span>Verifying your email…</span>
				</div>
			)}

			{state === "done" && (
				<>
					<h1>Email verified</h1>
					<div class="stack gap-3">
						<p class="muted">
							You're all set, {message}. Log in and start playing.
						</p>
						<div>
							<a href="/login" class="btn primary">
								Log in
							</a>
						</div>
					</div>
				</>
			)}

			{state === "failed" && (
				<>
					<h1>That link did not work</h1>
					<div class="stack gap-3">
						<p class="muted">{message}</p>
						<p class="muted">
							Log in with your email and password — the app will offer you a
							fresh link.
						</p>
						<div>
							<a href="/login" class="btn primary">
								Log in
							</a>
						</div>
					</div>
				</>
			)}
		</Shell>
	)
}

interface VerifyNoticeProps {
	email: string
	onLoggedOut: () => void
	// Re-checks the session, for the "I've verified, let me in" button — the
	// link is often clicked on a phone while this tab sits open on a laptop,
	// and nothing else would tell this tab about it. It must resolve only
	// once the check is in, or the "still not verified" hint below would
	// appear before the answer does. It resolves to an error message if the
	// check could not be made at all — which is not the same as "still
	// unverified", and must not be reported as it.
	onRecheck: () => Promise<string>
}

export function VerifyNotice({
	email,
	onLoggedOut,
	onRecheck,
}: VerifyNoticeProps) {
	const [sending, setSending] = useState(false)
	const [sent, setSent] = useState(false)
	const [error, setError] = useState("")
	// Set once a re-check has come back and we are still on this screen —
	// which can only mean the account is still unverified. Without it the
	// button looks broken: it does its job and nothing on screen moves.
	const [stillUnverified, setStillUnverified] = useState(false)

	async function handleRecheck() {
		setStillUnverified(false)
		setError("")
		const failure = await onRecheck()
		// The check never landed, so we learned nothing — saying "still not
		// verified" here would be a claim we cannot back up.
		if (failure) {
			setError(failure)
			return
		}
		setStillUnverified(true)
	}

	async function handleResend() {
		setSending(true)
		setError("")
		try {
			await resendVerification()
			setSent(true)
		} catch (err) {
			setError(err instanceof Error ? err.message : "Could not send the email")
		} finally {
			setSending(false)
		}
	}

	async function handleLogout() {
		await logout()
		onLoggedOut()
	}

	return (
		// Its own full-screen page, not the PublicShell frame: it is the whole
		// app view for a signed-in but unverified account, so it gets the same
		// centred-column treatment as Login rather than the document-shaped
		// PublicShell layout.
		<div class="h-100 overflow-auto">
			<div class="flex center jc-center py-3" style="min-height: 100%;">
				<div class="stack gap-4 p-4" style="width: 100%; max-width: 24rem;">
					<div class="text-center">
						<span class="inline-flex center gap-2">
							<img src="/static/brand/logo.svg" alt="" width="30" height="30" />
							<span class="brand-name">Quizfish</span>
						</span>
					</div>

					<h1>Check your email</h1>

					<div class="stack gap-3">
						<p class="muted">
							We sent a verification link to <strong>{email}</strong>. Click it
							and your account is ready — until then there is nothing to play.
						</p>

						{sent && (
							<div class="alert success">
								Sent again. It can take a minute to arrive.
							</div>
						)}
						{stillUnverified && (
							<div class="alert warning">
								Still not verified — open the link in the email first, then come
								back here.
							</div>
						)}
						{error && <div class="alert danger">{error}</div>}

						<div class="flex wrap gap-2">
							<button type="button" class="primary" onClick={handleRecheck}>
								I've verified — continue
							</button>
							<button type="button" onClick={handleResend} disabled={sending}>
								{sending ? "Sending…" : "Send the link again"}
							</button>
							<button type="button" class="link muted" onClick={handleLogout}>
								Log out
							</button>
						</div>

						<p class="muted small">
							Nothing in your inbox? Look in spam, and check the address above
							is the one you meant to use.
						</p>
					</div>

					<SiteFooter />
				</div>
			</div>
		</div>
	)
}
